import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Page from 'ha/modules/Page';
import { SEARCH } from 'ha/constants/pageNames';
import SomethingWentWrongPage from 'ha/pages/SomethingWentWrongPage';
import NotFoundPage from 'ha/pages/NotFound';
import UnsupportedCountries from 'ha/pages/UnsupportedCountries';
import { SearchBarProvider } from 'ha/modules/SearchBar';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import defaultFont from 'ha/constants/fonts/default';
import { sharpSansBold } from 'ha/constants/fonts/sourceSansHeaders';

import SearchLayout from './SearchLayout';
import SearchBar from './SearchBar';
import useSearchBar from '../hooks/useSearchBar';
import useFiltersAppliedAnalytics from '../hooks/useFiltersAppliedAnalytics';
import { initVisitedRooms } from '../actions';
import { getDateFilter, getCategoriesFilter } from '../selectors';

interface Props {
  error: boolean;
  notFound: boolean;
  isUnsupportedCountry: boolean;
  cityCanonical: string;
  countryCode: string;
  pageName: string;
  location: { query: { page: string }; pathname: string };
  listingsAnalytics: {
    numberOfSearchResults: number;
    numberOfPartnerListings: number;
    numberOfOrganicListings: number;
    isNumberOfSearchResultsExhaustive: boolean;
    listingIdResults: number[];
    areCountersOfOrganicAndPartnerListingsExhaustive: boolean;
  };
  getSearchAlert: () => void;
}

const SearchRoot: React.FC<Props> = ({
  error,
  notFound,
  isUnsupportedCountry,
  cityCanonical,
  countryCode,
  pageName,
  listingsAnalytics,
  getSearchAlert,
}) => {
  const dispatch = useDispatch();
  const { md } = useMediaQuery();
  const { searchLocationFilters } = useFeatureFlags();

  const { startDate, endDate } = useSelector(getDateFilter);
  const kindsFilter = useSelector(getCategoriesFilter);

  const searchBarProps = useSearchBar();
  useFiltersAppliedAnalytics();

  React.useEffect(() => {
    getSearchAlert();
  }, []);

  React.useEffect(() => {
    dispatch(initVisitedRooms());
  }, [dispatch]);

  if (error) {
    return <SomethingWentWrongPage />;
  }
  if (isUnsupportedCountry) {
    return <UnsupportedCountries />;
  }
  if (notFound) {
    return <NotFoundPage />;
  }

  const pageProps = {
    name: pageName,
    category: SEARCH,
    traits: {
      ...listingsAnalytics,
      cityCanonical,
      countryCode,
    },
    remarketingTag: {
      hrental_pagetype: 'searchresults',
      hrental_startdate: startDate && new Date(startDate).toISOString(),
      hrental_enddate: endDate && new Date(endDate).toISOString(),
      hrental_city: cityCanonical,
      hrental_country: countryCode,
      hrental_search_category: kindsFilter, // TODO: change to categories and map to old values
    },
    hasFooter: false,
    hasProgressBar: !md,
    hideIntercom: true,
    showSearchBar: searchLocationFilters !== 'on' || !md,
    searchBarElement:
      searchLocationFilters === 'on' && !md ? <SearchBar /> : undefined,
    hasStickyNav: md,
    activeFonts: [defaultFont, sharpSansBold],
    disableScrollToTop: true,
  };

  return (
    <SearchBarProvider value={searchBarProps}>
      <Page key={SEARCH} {...pageProps}>
        <SearchLayout />
      </Page>
    </SearchBarProvider>
  );
};

export default SearchRoot;
